import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FileText, CheckCircle2, Clock3, CalendarClock, XCircle, Zap, Cpu, AlertTriangle } from "lucide-react";
import { adminApi } from "@/lib/api";
import StatusPill from "@/components/admin/StatusPill";
import ApiUsagePanel from "@/components/admin/ApiUsagePanel";
import { timeAgo, formatDateTime } from "@/lib/format";

const CARDS = [
  { key: "stories_today", label: "Discovered Today (UTC)", icon: FileText },
  { key: "published_today", label: "Published Today (UTC)", icon: CheckCircle2 },
  { key: "total_published", label: "Total Published", icon: CheckCircle2 },
  { key: "ready_for_publish", label: "Ready to Publish", icon: CheckCircle2 },
  { key: "failed_publish", label: "Failed Publication", icon: AlertTriangle },
  { key: "waiting_pipeline", label: "Waiting Pipeline Stages", icon: Clock3 },
  { key: "translation_retry", label: "Translation Retry", icon: Clock3 },
  { key: "inactive_pipeline", label: "Inactive Pipeline Backlog", icon: Clock3 },
  { key: "held", label: "Held", icon: AlertTriangle },
  { key: "awaiting_review", label: "Legacy review backlog", icon: Clock3, highlight: true },
  { key: "scheduled", label: "Legacy scheduled", icon: CalendarClock },
  { key: "rejected", label: "Not Publishable / Low Value", icon: XCircle },
  { key: "quick_bites", label: "Quick Bites", icon: Zap },
  { key: "ai_processing", label: "Active Worker Leases", icon: Cpu },
  { key: "source_alerts", label: "Source Alerts", icon: AlertTriangle, alert: true },
];

export default function Dashboard() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["dashboard"], queryFn: adminApi.dashboard, retry: false, refetchInterval: 60000 });
  const translations = useQuery({ queryKey: ["translation-reliability"], queryFn: adminApi.translationReliability, retry: false });
  const usage = useQuery({ queryKey: ["admin-usage"], queryFn: adminApi.usage, retry: false, refetchInterval: 60000 });

  if (isError) return <div role="alert" className="text-[#A7ABB0]">Dashboard unavailable. <button onClick={() => refetch()} className="text-[#FF8A1F]">Retry loading</button></div>;
  if (isLoading || !data) return <p className="text-[#A7ABB0]">Loading dashboard…</p>;

  return (
    <div className="max-w-[1200px]">
      <section aria-label="Translation reliability" className="mb-6 p-4 border border-white/10 text-[#A7ABB0]">
        <h2 className="text-white font-bold">Translation reliability (read only)</h2>
        {translations.isError ? <p role="alert">Translation audit unavailable.</p> : !translations.data ? <p>Loading translation receipts…</p> : <>
          <p>Held observed: {translations.data.current_held_observed} · Known invalid output: {translations.data.ledger_classifications.KNOWN_INVALID_OUTPUT || 0} · Ambiguous calls: {translations.data.ledger_classifications.AMBIGUOUS_NO_REPLAY || 0}</p>
          <p>Locally recoverable: requires saved-result validation. No replay authorized.</p>
          {translations.data.coverage.partial && <p>Partial scan; counts are lower bounds.</p>}
          <ul>{translations.data.historical_failure_receipts.map((row, i) => <li key={i}>{row.requested_model_family} → {row.response_model_family}: {row.category} / {row.validation_reason}: {row.count}</li>)}</ul>
        </>}
      </section>
      <header className="mb-8">
        <div className="td-eyebrow mb-2" style={{ color: "#FF8A1F" }}>
          AI News Aggregator
        </div>
        <h1 className="font-display font-extrabold text-white text-3xl tracking-tight">Dashboard</h1>
        <p className="text-[#71767c] mt-1 font-mono-td text-xs">Daily KPI boundaries: UTC · Activity timestamps: your browser local time · EN/ID articles count once</p>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        {CARDS.map(({ key, label, icon: Icon, highlight, alert }) => {
          const value = data.cards[key] ?? 0;
          const isAlert = alert && value > 0;
          return (
            <div
              key={key}
              className="rounded-lg border p-4"
              style={{
                borderColor: isAlert ? "rgba(248,113,113,0.3)" : highlight && value > 0 ? "rgba(255,138,31,0.3)" : "rgba(255,255,255,0.08)",
                background: "#111315",
              }}
              title={data.kpi_definitions?.[key]}
              data-testid={`dash-card-${key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <Icon size={16} style={{ color: isAlert ? "#f87171" : highlight ? "#FF8A1F" : "#71767c" }} />
              </div>
              <div className="font-display font-extrabold text-white" style={{ fontSize: "1.9rem", lineHeight: 1 }}>
                {value}
              </div>
              <div className="td-eyebrow mt-2" style={{ color: "#A7ABB0", fontSize: "0.55rem" }}>
                {label}
              </div>
              <p className="text-xs text-[#71767c] mt-2">{data.kpi_definitions?.[key]}</p>
              {key === "ready_for_publish" && <Link to="/admin/queue?view=ready" className="text-xs text-[#FF8A1F]">Open ready articles →</Link>}
            </div>
          );
        })}
      </div>

      <ApiUsagePanel usage={usage.data} isLoading={usage.isLoading} unavailable={usage.isError} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent activity */}
        <div className="lg:col-span-2 rounded-lg border" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#111315" }}>
          <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <h2 className="font-display font-bold text-white">Recent Activity</h2>
            <Link to="/admin/queue" className="td-eyebrow" style={{ color: "#FF8A1F", fontSize: "0.58rem" }}>
              Editorial Queue →
            </Link>
          </div>
          <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            {data.recent_activity.map((a, i) => (
              <li key={i} className="px-5 py-3.5 flex items-center gap-3">
                <StatusPill status={a.processing_label || a.status} small />
                <Link to={a.story_id ? `/admin/workflows/${a.story_id}` : `/admin/queue/${a.candidate_id || a.id}`} className="text-white text-sm flex-1 truncate font-display">{a.headline}</Link>
                <span title={formatDateTime(a.occurred_at || a.created_at)} className="td-eyebrow shrink-0" style={{ color: "#71767c", fontSize: "0.52rem" }}>
                  {timeAgo(a.occurred_at || a.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right column: system status + schedule */}
        <div className="space-y-6">
          <div className="rounded-lg border" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#111315" }}>
            <div className="px-5 py-4 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <h2 className="font-display font-bold text-white">System Status</h2>
            </div>
            <ul className="p-3 space-y-1">
              {data.system_status.map((s, i) => (
                <li key={i} className="flex items-center gap-2.5 px-2 py-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ({ healthy: '#3ecf8e', configured: '#4ea3ff',
                    degraded: '#f5c451', unverified: '#71767c', not_configured: '#71767c', disabled: '#71767c' })[s.state] || '#71767c' }} />
                  <span className="text-[#D5D5D0] text-sm flex-1">{s.name}</span>
                  <span className="font-mono-td" style={{ color: "#71767c", fontSize: "0.62rem" }}>
                    {s.status}
                    {s.model && <span className="block mt-1 break-all">Model: {s.model}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#111315" }}>
            <div className="px-5 py-4 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
              <h2 className="font-display font-bold text-white">Historical schedule</h2>
            </div>
            <ul className="p-3 space-y-1">
              {data.schedule.length === 0 && <li className="px-2 py-2 text-[#71767c] text-sm">Nothing scheduled.</li>}
              {data.schedule.map((s) => (
                <li key={s.id} className="px-2 py-2">
                  <Link to={`/admin/queue/${s.id}`} className="flex items-center gap-2">
                    <StatusPill status={s.status} small />
                    <span className="text-[#D5D5D0] text-sm flex-1 truncate hover:text-white">{s.headline}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

